// 選択時スタイルパネルの表向けカテゴリへ置く、セル編集の入口と行・列の増減ボタン。
//
// ショートカット（F2・Tab / Enter）を知らなくても表を編集できるよう、単独選択の表では
// 常に「セルを編集」から入力へ入れるようにする。ロック中・参照専用では親コンポーネントが
// パネル自体を描画しないため、ここでは「無効なボタンを並べる」のではなく入口を持たない。

import { Button } from "@meiden-connect/ui";
import {
  TABLE_CELL_MAX_LENGTH,
  TABLE_MAX_COLUMNS,
  TABLE_MAX_ROWS,
  type TableCanvasObject,
} from "@refinement-canvas/domain";
import { beginTableCellEdit } from "../input/use-table-cell-editor";
import { useTableCellEditorStore } from "../input/table-cell-editor-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

/** 表 1 つに対する行・列の増減操作。複数選択では「セルを編集」を無効にする。 */
export function TableShapeActions({
  table,
  singleSelection,
  dispatcher,
}: {
  readonly table: TableCanvasObject;
  readonly singleSelection: boolean;
  readonly dispatcher: CanvasOpDispatcher;
}): JSX.Element {
  const rowCount = table.cells.length;
  const columnCount = table.cells[0]?.length ?? 0;
  return (
    <>
      <TableActionButton
        label="セルを編集"
        glyph="✎"
        testId="table-action-edit-cell"
        disabled={!singleSelection}
        onClick={() => beginTableCellEdit(table.id, 0, 0)}
      />
      <TableActionButton
        label="行を追加"
        glyph="＋行"
        testId="table-action-add-row"
        disabled={rowCount >= TABLE_MAX_ROWS}
        onClick={() => dispatcher.updateTableShape(table.id, { kind: "insert-row", at: rowCount })}
      />
      <TableActionButton
        label="最終行を削除"
        glyph="－行"
        testId="table-action-delete-row"
        // ヘッダ行は削除できない（行数が 1 のときは無効）。
        disabled={rowCount <= 1}
        onClick={() =>
          dispatcher.updateTableShape(table.id, { kind: "delete-row", at: rowCount - 1 })
        }
      />
      <TableActionButton
        label="列を追加"
        glyph="＋列"
        testId="table-action-add-column"
        disabled={columnCount >= TABLE_MAX_COLUMNS}
        onClick={() =>
          dispatcher.updateTableShape(table.id, { kind: "insert-column", at: columnCount })
        }
      />
      <TableActionButton
        label="右端の列を削除"
        glyph="－列"
        testId="table-action-delete-column"
        disabled={columnCount <= 1}
        onClick={() =>
          dispatcher.updateTableShape(table.id, { kind: "delete-column", at: columnCount - 1 })
        }
      />
    </>
  );
}

function TableActionButton({
  label,
  glyph,
  testId,
  disabled,
  onClick,
}: {
  readonly label: string;
  readonly glyph: string;
  readonly testId: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <Button
      type="button"
      // 表示文言どおりの処理をクリック直後に実行するため action を使う（子パネルを開かない）。
      variant="action"
      className="canvas-icon-button canvas-toolbar-parent-trigger canvas-toolbar-square-button"
      aria-label={label}
      title={label}
      disabled={disabled}
      data-testid={testId}
      onClick={onClick}
    >
      <span aria-hidden="true" className="canvas-table-action-glyph">
        {glyph}
      </span>
    </Button>
  );
}

/**
 * インライン入力が使えない表（極小・低倍率・画面外）向けの補助入力欄。
 *
 * インライン入力と同じ状態・履歴・IME・送信キューを使い、入力 DOM を同時に 2 個有効にしない。
 * 表の回転・縮尺・clip を継承せず、パネルの通常レイアウト内へ置く。
 */
export function TableCellAuxiliaryInput(): JSX.Element | null {
  const handle = useTableCellEditorStore((state) => state.handle);
  const auxiliary = useTableCellEditorStore((state) => state.auxiliary);
  const draft = useTableCellEditorStore((state) => state.draft);
  if (!handle || !auxiliary) return null;
  return (
    <div
      className="canvas-table-cell-auxiliary"
      data-table-cell-auxiliary="true"
      data-testid="table-cell-auxiliary"
    >
      <span className="canvas-table-cell-auxiliary__label">
        {`${handle.row + 1} 行 ${handle.column + 1} 列`}
      </span>
      <input
        type="text"
        // 自動フォーカスは、インライン入力が使えないと判定した直後に入力を続けられるようにする。
        autoFocus
        className="canvas-table-cell-auxiliary__input"
        value={draft}
        maxLength={TABLE_CELL_MAX_LENGTH}
        aria-label={`表のセル ${handle.row + 1} 行 ${handle.column + 1} 列`}
        data-testid="table-cell-auxiliary-input"
        // インライン入力と同じハンドラを使う（IME・改行貼り付けの畳み込み・セル内 Undo / Redo・
        // セル移動・送信キューの扱いを、表示方式で分岐させない）。
        {...handle.inputHandlers}
      />
      <Button
        type="button"
        variant="action"
        className="canvas-icon-button canvas-toolbar-square-button"
        aria-label="前のセルへ"
        title="前のセルへ"
        data-testid="table-cell-auxiliary-previous"
        onClick={() => handle.moveCell("previous")}
      >
        <span aria-hidden="true">◀</span>
      </Button>
      <Button
        type="button"
        variant="action"
        className="canvas-icon-button canvas-toolbar-square-button"
        aria-label="次のセルへ"
        title="次のセルへ"
        data-testid="table-cell-auxiliary-next"
        onClick={() => handle.moveCell("next")}
      >
        <span aria-hidden="true">▶</span>
      </Button>
    </div>
  );
}
